import React from 'react';
import {
  ExclamationCircleOutlined,
  RightOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import styles from './PanelList.module.scss';

export const PanelPage: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className={styles.page}>{children}</div>;

interface PanelHeadProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  count?: number;
  extra?: React.ReactNode;
  action?: React.ReactNode;
  compact?: boolean;
}

export const PanelHead: React.FC<PanelHeadProps> = ({
  icon,
  title,
  count,
  extra,
  action,
  compact,
}) => (
  <div className={clsx(styles.head, compact && styles.compact)}>
    <span className={styles.headTitle}>
      {icon}
      {title}
      {count != null && <span className={styles.count}>{count}</span>}
      {extra}
    </span>
    {action}
  </div>
);

export const PanelList: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className={styles.list}>{children}</div>;

type LeadVariant = 'default' | 'owl' | 'pdf' | 'excel' | 'zip' | 'image';

export const PanelLead: React.FC<{
  variant?: LeadVariant;
  round?: boolean;
  children: React.ReactNode;
}> = ({ variant = 'default', round, children }) => (
  <span
    className={clsx(
      styles.lead,
      round && styles.round,
      variant !== 'default' && styles[variant]
    )}
  >
    {children}
  </span>
);

interface PanelRowProps {
  lead?: React.ReactNode;
  title: React.ReactNode;
  desc?: React.ReactNode;
  meta?: React.ReactNode;
  trail?: React.ReactNode;
  clickable?: boolean;
  dim?: boolean;
  onClick?: () => void;
}

export const PanelRow: React.FC<PanelRowProps> = ({
  lead,
  title,
  desc,
  meta,
  trail,
  clickable,
  dim,
  onClick,
}) => {
  const className = clsx(
    styles.row,
    clickable && styles.clickable,
    dim && styles.dim
  );
  const inner = (
    <>
      {lead}
      <span className={styles.title}>{title}</span>
      {desc != null && <span className={styles.desc}>{desc}</span>}
      {meta != null && <span className={styles.meta}>{meta}</span>}
      {trail != null && <span className={styles.trail}>{trail}</span>}
    </>
  );
  if (clickable) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {inner}
      </button>
    );
  }
  return <div className={className}>{inner}</div>;
};

export const PanelDot: React.FC = () => <span className={styles.dot}>·</span>;

export const PanelChevron: React.FC = () => (
  <RightOutlined className={styles.chev} />
);

type PillTone =
  | 'open'
  | 'deferred'
  | 'resolved'
  | 'processing'
  | 'error'
  | 'ready';

export const PanelPill: React.FC<{
  tone: PillTone;
  children: React.ReactNode;
}> = ({ tone, children }) => (
  <span className={clsx(styles.pill, styles[tone])}>{children}</span>
);

export const PanelStatus: React.FC<{
  state: 'processing' | 'error' | 'ready';
}> = ({ state }) => {
  if (state === 'processing') {
    return (
      <span className={clsx(styles.status, styles.proc)}>
        <SyncOutlined spin />
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className={clsx(styles.status, styles.err)}>
        <ExclamationCircleOutlined />
      </span>
    );
  }
  return (
    <>
      <span className={styles.readyDot} />
      <RightOutlined className={styles.go} />
    </>
  );
};
